/**
 * Il pannello senza un'azienda scelta: tre numeri (aziende, account, persone al lavoro), gli account per livello in una
 * barra sola, come si leggono i segni, l'elenco delle aziende e, nella console, quelle che non stanno sulla mappa.
 */
import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LIVELLI_MAPPA, type LivelloMappa } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { COLORE_LIVELLO } from './aspetto';
import { ElencoAziende, ordinaPerAccount } from './ElencoAziende';
import type { FormatiMappa } from './formati';
import { PallinoAdesso } from './PallinoAdesso';
import type { AziendaNellaVista } from './tipi';

interface Props {
  aziende: readonly AziendaNellaVista[];
  giorni: number;
  minutiAdesso: number;
  /** Ci sono archi di fornitura sulla carta: la legenda li spiega. */
  conLegami?: boolean;
  selezionata: string | null;
  onScegli: (chiave: string) => void;
  formati: FormatiMappa;
}

export function totaliDelleAziende(aziende: readonly AziendaNellaVista[]) {
  const perLivello: Record<LivelloMappa, number> = { free: 0, base: 0, pro: 0, business: 0 };
  let account = 0;
  let attive = 0;
  let adesso = 0;
  for (const a of aziende) {
    account += a.account_totale;
    attive += a.persone_attive;
    adesso += a.persone_adesso;
    for (const l of LIVELLI_MAPPA) perLivello[l] += a.account[l] ?? 0;
  }
  return { aziende: aziende.length, account, attive, adesso, perLivello };
}

export const Titoletto: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="h3" sx={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.color.graphite, mb: 1.25 }}>
    {children}
  </Typography>
);

const Riga: React.FC<{ numero: string; testo: string; adesso?: boolean }> = ({ numero, testo, adesso = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.9, '& + &': { borderTop: `1px solid ${tokens.color.lineSoft}` } }}>
    <Typography
      component="span"
      sx={{
        fontFamily: tokens.font.display,
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1,
        letterSpacing: '-0.02em',
        minWidth: 64,
        fontVariantNumeric: 'tabular-nums',
        fontVariationSettings: '"SOFT" 30',
      }}
    >
      {numero}
    </Typography>
    <Typography component="span" sx={{ fontSize: '0.88rem', color: adesso ? tokens.color.graphite : tokens.color.ash, fontWeight: adesso ? 600 : 400, lineHeight: 1.35, display: 'inline-flex', alignItems: 'center', gap: 0.9 }}>
      {adesso && <PallinoAdesso />}
      {testo}
    </Typography>
  </Box>
);

/** La barra degli account per livello: un segmento per livello, Free a solo contorno come sulla mappa. */
export const BarraDeiLivelli: React.FC<{ perLivello: Record<LivelloMappa, number>; formati: FormatiMappa }> = ({ perLivello, formati }) => {
  const { t } = useTranslation('mappa');
  const totale = LIVELLI_MAPPA.reduce((s, l) => s + perLivello[l], 0);
  const presenti = LIVELLI_MAPPA.filter((l) => perLivello[l] > 0);
  if (totale === 0) return null;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '3px', height: 10 }} role="img" aria-label={presenti.map((l) => t('riepilogo.livello_conto', { livello: t(`livelli.${l}`), numero: formati.numero(perLivello[l]) })).join(', ')}>
        {presenti.map((l) => (
          <Box
            key={l}
            sx={{
              flex: `${perLivello[l]} 0 0`,
              minWidth: 6,
              borderRadius: '3px',
              bgcolor: l === 'free' ? tokens.color.paper : COLORE_LIVELLO[l],
              border: l === 'free' ? `1.5px solid ${COLORE_LIVELLO.free}` : 'none',
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 1 }}>
        {presenti.map((l) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              component="span"
              aria-hidden
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l === 'free' ? 'transparent' : COLORE_LIVELLO[l], border: `1.5px solid ${COLORE_LIVELLO[l]}` }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: tokens.color.ash }}>
              {t(`livelli.${l}`)}{' '}
              <Box component="span" sx={{ color: tokens.color.graphite, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formati.numero(perLivello[l])}</Box>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** Il segno disegnato in piccolo, con le sue tre regole. */
const ComeSiLegge: React.FC<{ giorni: number; minuti: number; conLegami: boolean }> = ({ giorni, minuti, conLegami }) => {
  const { t } = useTranslation('mappa');
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box component="svg" viewBox="-26 -26 52 52" width={52} height={52} aria-hidden sx={{ flexShrink: 0 }}>
        <circle r={22} fill={`${COLORE_LIVELLO.business}24`} />
        <circle r={21} fill="none" stroke={COLORE_LIVELLO.business} strokeWidth={2} />
        <circle r={14} fill={COLORE_LIVELLO.business} />
        <rect x={-9} y={-9} width={18} height={18} rx={4} fill={tokens.color.paper} />
        <text textAnchor="middle" dominantBaseline="central" style={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: 10, fill: COLORE_LIVELLO.business }}>A</text>
      </Box>
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', '& li': { fontSize: '0.8rem', color: tokens.color.ash, lineHeight: 1.45 }, '& li + li': { mt: 0.5 } }}>
        <li>{t('riepilogo.come_alone')}</li>
        <li>{t('riepilogo.come_nucleo', { giorni })}</li>
        <li>{t('riepilogo.come_colore')}</li>
        <li>{t('riepilogo.come_polso', { minuti })}</li>
        <li>{t('riepilogo.come_sedi')}</li>
        {conLegami && <li>{t('riepilogo.come_archi')}</li>}
      </Box>
    </Box>
  );
};

export const PannelloRiepilogo: React.FC<Props> = ({ aziende, giorni, minutiAdesso, conLegami = false, selezionata, onScegli, formati }) => {
  const { t } = useTranslation('mappa');
  const sullaMappa = aziende.filter((a) => a.sedi.length > 0);
  const fuori = aziende.filter((a) => a.sedi.length === 0);
  const totali = totaliDelleAziende(aziende);
  return (
    <Box>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Riga numero={formati.numero(totali.aziende)} testo={t('riepilogo.aziende', { count: totali.aziende })} />
        <Riga numero={formati.numero(totali.account)} testo={t('riepilogo.account', { count: totali.account })} />
        <Riga numero={formati.numero(totali.attive)} testo={t('riepilogo.persone', { count: totali.attive, giorni })} />
        <Riga numero={formati.numero(totali.adesso)} testo={t('riepilogo.adesso', { count: totali.adesso })} adesso />
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Titoletto>{t('riepilogo.account_per_livello')}</Titoletto>
        <BarraDeiLivelli perLivello={totali.perLivello} formati={formati} />
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Titoletto>{t('riepilogo.come_si_legge')}</Titoletto>
        <ComeSiLegge giorni={giorni} minuti={minutiAdesso} conLegami={conLegami} />
      </Box>

      {sullaMappa.length > 0 && (
        <Box sx={{ pb: 1 }}>
          <Box sx={{ px: 2.5 }}><Titoletto>{t('riepilogo.elenco')}</Titoletto></Box>
          <ElencoAziende aziende={ordinaPerAccount(sullaMappa)} selezionata={selezionata} onScegli={onScegli} formati={formati} />
        </Box>
      )}

      {fuori.length > 0 && (
        <Box sx={{ px: 2.5, pt: 1.5, pb: 2.5, borderTop: `1px solid ${tokens.color.line}` }}>
          <Titoletto>{t('riepilogo.senza_posizione')}</Titoletto>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {fuori.map((a) => (
              <Box component="li" key={a.chiave}>
                <ButtonBase
                  onClick={() => onScegli(a.chiave)}
                  sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', py: 0.75, minHeight: 40, borderRadius: 1, '&:hover': { bgcolor: tokens.color.bone } }}
                >
                  <Typography sx={{ fontSize: '0.82rem', color: tokens.color.graphite }}>
                    {a.nome}
                    {a.motivo_senza_posizione && (
                      <Box component="span" sx={{ color: tokens.color.ash }}>{`: ${t(`riepilogo.motivi.${a.motivo_senza_posizione}`)}`}</Box>
                    )}
                  </Typography>
                </ButtonBase>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
